import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Store, Loader2, RefreshCw, AlertTriangle, Check, Plus, Copy, Trash2, Save,
  ChevronUp, ChevronDown, Search,
} from 'lucide-react';
import { apiService, ConfigInspection, ConfigSectionsQuery, ContainerApplyResult } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { loadPackPalette, PalettePickEntry } from '@/components/config/packPalette';
import { SearchPicker, PickerOption } from '@/components/config/SearchPicker';
import { ApplyReport, ContainerScope, NoReference } from '@/components/config/AllContainers';
import styles from './LootboxPage.module.scss';

// ============================================================
// Shop admin (adminshop.config.json), commun à tous les containers du projet
// ============================================================
// Structure : { economySnapshotTime, modifier, items: [ { item, category, buyPrice, canBuy,
// canSell, baseSellPrice, maxSellPrice } ] }.
//
// Pas de choix de container : la page lit le fichier du container de référence (le premier, par
// id, qui le porte) et l'enregistrement écrit items, modifier et economySnapshotTime dans CHAQUE
// container du projet qui le porte. Un champ vidé est retiré partout.
//
// Les prix de vente peuvent être décimaux (maxSellPrice: 17.5), donc rien n'est arrondi.
// Tout ce que le formulaire ne connaît pas est PRÉSERVÉ : les autres clés à la racine du document
// restent propres à chaque container, et les clés inconnues de chaque entrée d'item sont réécrites
// telles quelles.
// ============================================================

/** Le seul fichier édité par cette page, avec ou sans extension .json. */
const SHOP_CONFIG = 'adminshop.config';

/** Sections gérées par la page ; le reste du fichier n'est ni lu ni écrit. */
const SHOP_KEYS = ['items', 'modifier', 'economySnapshotTime'] as const;

const SHOP_QUERY: ConfigSectionsQuery = {
  fileNames: [`${SHOP_CONFIG}.json`, SHOP_CONFIG],
  keys: [...SHOP_KEYS],
};

const SHOP_LABELS: Record<string, string> = {
  items: 'catalogue des items',
  modifier: 'modificateur global',
  economySnapshotTime: "fenêtre d'économie",
};

/** Catégories connues du shop. La liste s'élargit avec celles réellement présentes dans le fichier. */
const KNOWN_CATEGORIES = ['BLOCK', 'ORE', 'VEGETABLE', 'OTHER'] as const;

interface ShopItem {
  item?: string;
  category?: string;
  buyPrice?: number;
  canBuy?: boolean;
  canSell?: boolean;
  baseSellPrice?: number;
  maxSellPrice?: number;
  [k: string]: unknown;
}

/** Ce que la page édite : items / economySnapshotTime / modifier (le serveur ne renvoie rien d'autre). */
interface ShopFile {
  economySnapshotTime?: number;
  modifier?: number;
  items?: ShopItem[];
  [k: string]: unknown;
}

/** Champ numérique : '' quand la clé est absente, pour distinguer « vide » de « zéro ». */
function numOrEmpty(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? String(v) : '';
}

/**
 * Incohérences qui produiraient un shop bancal en jeu, signalées sans bloquer l'édition.
 * Le panel n'a aucun test : autant dire tout de suite ce qui ne tient pas debout.
 */
function warningsOf(r: ShopItem, duplicate: boolean): string[] {
  const out: string[] = [];
  if (!r.item || !String(r.item).trim()) out.push('aucun item');
  if (duplicate) out.push('item en doublon');
  const base = r.baseSellPrice;
  const max = r.maxSellPrice;
  if (typeof base === 'number' && typeof max === 'number' && base > max) {
    out.push('prix de vente de base supérieur au maximum');
  }
  if (r.canSell && typeof base !== 'number' && typeof max !== 'number') {
    out.push('vendable sans prix de vente');
  }
  if (r.canBuy && typeof r.buyPrice !== 'number') out.push("achetable sans prix d'achat");
  return out;
}

export function AdminShopPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [inspection, setInspection] = useState<ConfigInspection | null>(null);
  const [doc, setDoc] = useState<ShopFile | null>(null);
  const [orig, setOrig] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [report, setReport] = useState<ContainerApplyResult | null>(null);
  const [palette, setPalette] = useState<PalettePickEntry[]>([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [query, setQuery] = useState('');
  /** Dernière lecture lancée : une réponse plus ancienne (projet précédent) est ignorée. */
  const loadSeq = useRef(0);

  // Même liste que les catalogues Blocks / Items du projet, avec les vignettes.
  useEffect(() => {
    loadPackPalette(activeGameId).then(setPalette).catch(() => setPalette([]));
  }, [activeGameId]);

  /**
   * Identifiants déjà présents dans le fichier TEL QU'IL A ÉTÉ CHARGÉ. Volontairement dérivé de
   * `orig` et non de `doc` : le picker écrit dans row.item à chaque frappe, donc partir du
   * document en cours d'édition ferait entrer des identifiants partiels dans la liste.
   */
  const fileItemIds = useMemo<string[]>(() => {
    if (!orig) return [];
    try {
      const parsed = JSON.parse(orig) as ShopFile;
      const out: string[] = [];
      for (const r of parsed.items ?? []) {
        const id = String(r?.item ?? '').trim();
        if (id && !out.includes(id)) out.push(id);
      }
      return out;
    } catch { return []; }
  }, [orig]);

  /**
   * Options du picker : la palette des packs (avec vignettes), COMPLÉTÉE par les identifiants du
   * fichier. Le shop est majoritairement vanilla et minecraft:sand / minecraft:coal ne sont dans
   * aucun pack : sans ce complément la liste ne proposerait que les items Paladium, et il faudrait
   * ressaisir chaque identifiant vanilla à la main, à la faute de frappe près.
   */
  const itemOptions = useMemo<PickerOption[]>(() => {
    const out: PickerOption[] = palette.map((p) => ({
      value: p.id,
      label: p.id,
      hint: p.kind === 'block' ? 'block' : 'item',
      thumbnailPath: p.path,
      groupId: p.groupId,
    }));
    const seen = new Set(out.map((o) => o.value));
    for (const id of fileItemIds) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ value: id, label: id, hint: 'déjà dans le shop' });
    }
    return out;
  }, [palette, fileItemIds]);

  /**
   * Lit le shop dans le container de référence et l'alignement des autres. Le fichier est CHERCHÉ
   * côté serveur dans chaque container sur le suffixe de son chemin (n'importe quel dossier,
   * n'importe quelle casse). `keepEdit` ne rafraîchit que le bandeau, après un enregistrement.
   */
  const load = useCallback(async (keepEdit = false) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    if (!keepEdit) {
      setStatus(null);
      setReport(null);
      setDoc(null);
    }
    const r = await apiService.inspectContainerConfig(SHOP_QUERY);
    if (seq !== loadSeq.current) return;
    setLoading(false);
    setInspection(r.data ?? null);
    if (keepEdit) return;
    if (!r.data) { setStatus({ ok: false, msg: r.error ?? 'Lecture impossible.' }); return; }
    if (!r.data.referenceContainerId) return; // aucun container ne porte le fichier : dit plus bas
    const sections = (r.data.sections ?? {}) as ShopFile;
    if (!Array.isArray(sections.items)) {
      setStatus({ ok: false, msg: `${r.data.referencePath ?? SHOP_CONFIG} ne contient pas de liste « items ».` });
      return;
    }
    setDoc(sections);
    setOrig(JSON.stringify(sections));
  }, []);

  useEffect(() => { void load(); }, [activeGameId, load]);

  const items: ShopItem[] = doc?.items ?? [];
  /** Autres sections du fichier : propres à chaque container, jamais envoyées ici. */
  const otherKeys = inspection?.otherKeys ?? [];

  const categories = useMemo(() => {
    const set = new Set<string>(KNOWN_CATEGORIES);
    items.forEach((r) => { if (r.category) set.add(String(r.category)); });
    return [...set].sort();
  }, [items]);

  /** Items apparaissant plus d'une fois : un shop à deux entrées pour le même item est ambigu. */
  const duplicates = useMemo(() => {
    const seen = new Map<string, number>();
    items.forEach((r) => {
      const id = String(r.item ?? '').trim();
      if (id) seen.set(id, (seen.get(id) ?? 0) + 1);
    });
    return new Set([...seen.entries()].filter(([, n]) => n > 1).map(([id]) => id));
  }, [items]);

  /**
   * Les lignes affichées, avec leur index RÉEL dans le document : éditer par l'index de la liste
   * filtrée écrirait dans la mauvaise entrée dès qu'un filtre est actif.
   */
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => !categoryFilter || row.category === categoryFilter)
      .filter(({ row }) => !q || String(row.item ?? '').toLowerCase().includes(q));
  }, [items, categoryFilter, query]);

  const warnCount = useMemo(
    () => items.filter((r) => warningsOf(r, duplicates.has(String(r.item ?? '').trim())).length > 0).length,
    [items, duplicates],
  );

  const setItems = (next: ShopItem[]) => setDoc((prev) => (prev ? { ...prev, items: next } : prev));

  const mutate = (i: number, patch: (r: ShopItem) => ShopItem) => {
    const next = [...items];
    next[i] = patch({ ...next[i] });
    setItems(next);
  };

  /**
   * Écrit un champ de prix, ou retire la clé quand le champ est vidé : un prix absent et un prix à
   * zéro ne veulent pas dire la même chose pour le mod.
   */
  const setNumber = (i: number, key: 'buyPrice' | 'baseSellPrice' | 'maxSellPrice', raw: string) => {
    mutate(i, (r) => {
      const out: ShopItem = { ...r };
      if (raw.trim() === '') delete out[key];
      else {
        const n = Number(raw);
        if (Number.isFinite(n)) out[key] = n;
      }
      return out;
    });
  };

  const addRow = () => {
    setItems([...items, {
      item: '',
      category: categoryFilter || 'OTHER',
      buyPrice: 0,
      canBuy: true,
      canSell: true,
      baseSellPrice: 0,
      maxSellPrice: 0,
    }]);
    // La nouvelle ligne doit rester visible même si une recherche est active.
    setQuery('');
  };

  const duplicate = (i: number) => {
    const next = [...items];
    next.splice(i + 1, 0, JSON.parse(JSON.stringify(next[i])) as ShopItem);
    setItems(next);
  };

  const remove = (i: number) => setItems(items.filter((_, k) => k !== i));

  /**
   * Déplace la ligne vers sa voisine AFFICHÉE, pas vers l'index brut adjacent : sous un filtre de
   * catégorie ou une recherche, échanger avec le voisin du tableau complet déplace la ligne derrière
   * une entrée masquée, donc le bouton paraît ne rien faire. Sans filtre, `visible` est la liste
   * entière et le comportement est identique à un simple échange voisin.
   */
  const move = (i: number, d: number) => {
    const pos = visible.findIndex((v) => v.index === i);
    const target = visible[pos + d];
    if (pos < 0 || !target) return;
    const next = [...items];
    [next[i], next[target.index]] = [next[target.index], next[i]];
    setItems(next);
  };

  const setRoot = (key: 'economySnapshotTime' | 'modifier', raw: string) => {
    setDoc((prev) => {
      if (!prev) return prev;
      const out = { ...prev };
      if (raw.trim() === '') delete out[key];
      else {
        const n = Number(raw);
        if (Number.isFinite(n)) out[key] = n;
      }
      return out;
    });
  };

  const serialized = doc ? JSON.stringify(doc) : '';
  const dirty = !!doc && serialized !== orig;

  /**
   * Écrit le shop dans chaque container du projet qui porte le fichier. Un champ racine vidé (donc
   * absent du document affiché) est retiré partout : c'est ce que montre la page qui fait foi.
   */
  const save = async () => {
    if (!doc) return;
    setSaving(true);
    setStatus(null);
    setReport(null);
    const sections: Record<string, unknown> = {};
    const removeKeys: string[] = [];
    for (const k of SHOP_KEYS) {
      if (doc[k] !== undefined) sections[k] = doc[k];
      else removeKeys.push(k);
    }
    const res = await apiService.applyContainerConfig({ ...SHOP_QUERY, sections, removeKeys });
    setSaving(false);
    if (!res.data) {
      setStatus({ ok: false, msg: res.error ?? 'Enregistrement en échec' });
      return;
    }
    setReport(res.data);
    // Un container en échec garde la page « non enregistrée » : un nouvel essai réécrit partout.
    if (res.data.success) setOrig(serialized);
    void load(true);
  };

  /** Relit le shop ; ne jette pas des modifications sans le demander. */
  const reload = () => {
    if (dirty && !window.confirm('Recharger le shop abandonnera les modifications non enregistrées. Continuer ?')) return;
    void load();
  };

  /** Lecture faite, mais aucun container du projet ne porte le fichier : le pourquoi, container par container. */
  const missing = inspection && !inspection.referenceContainerId ? inspection : null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Shop admin</h1>
          <p className={styles.subtitle}>
            Prix d'achat et de vente du shop administrateur, communs à tous les containers du projet.
            Le reste du fichier n'est ni affiché ni modifié.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.secondaryBtn} onClick={reload} disabled={loading}>
            {loading ? <Loader2 size={14} className={styles.spin} /> : <RefreshCw size={14} />} Rafraîchir
          </button>
          <button className={styles.primaryBtn} onClick={save} disabled={!dirty || saving}
            title="Écrit le shop dans tous les containers du projet qui portent adminshop.config">
            {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} Enregistrer
          </button>
        </div>
      </header>

      {inspection && (
        <ContainerScope entries={inspection.entries} referencePath={inspection.referencePath}
          labelOf={(k) => SHOP_LABELS[k] ?? k} />
      )}

      {status && (
        <div className={status.ok ? styles.ok : styles.error}>
          {status.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {status.msg}
        </div>
      )}

      {report && <ApplyReport result={report} onClose={() => setReport(null)} />}

      <section className={styles.detail}>
        {!doc ? (
          <div className={styles.detailEmpty}>
            {loading ? (
              <><Loader2 size={22} className={styles.spin} /><p>Ouverture de adminshop.config.json…</p></>
            ) : missing && missing.entries.length === 0 ? (
              <>
                <AlertTriangle size={22} />
                <p>{missing.message}</p>
                <p className={styles.muted}>Déclare les containers du projet dans Paramètres généraux → Containers.</p>
              </>
            ) : missing ? (
              <NoReference file="adminshop.config.json" entries={missing.entries} />
            ) : (
              <>
                <Store size={26} />
                <p>adminshop.config.json n'a pas pu être ouvert.</p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className={styles.boxHead}>
              <label className={styles.field}>
                <span className={styles.label}>Fenêtre d'économie (minutes)</span>
                <input className={styles.input} type="number" min={0}
                  value={numOrEmpty(doc.economySnapshotTime)}
                  onChange={(e) => setRoot('economySnapshotTime', e.target.value)}
                  placeholder="ex. 10080 (7 jours)" />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Modificateur global</span>
                <input className={styles.input} type="number" step="any" min={0}
                  value={numOrEmpty(doc.modifier)}
                  onChange={(e) => setRoot('modifier', e.target.value)}
                  placeholder="ex. 1.2" />
              </label>
              <div className={styles.totals}>
                <span><strong>{items.length}</strong> item(s)</span>
                <span><strong>{categories.length}</strong> catégorie(s)</span>
                {warnCount > 0 && <span><strong>{warnCount}</strong> à vérifier</span>}
              </div>
            </div>

            <div className={styles.boxHead}>
              <label className={styles.field}>
                <span className={styles.label}>Catégorie</span>
                <select className={styles.input} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                  <option value="">toutes</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Rechercher un item</span>
                <div className={styles.search}>
                  <Search size={14} />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="paladium:…" />
                </div>
              </label>
              <div className={styles.totals}>
                <span><strong>{visible.length}</strong> affiché(s)</span>
              </div>
            </div>

            {otherKeys.length > 0 && (
              <p className={styles.muted}>
                Sections propres à chaque container, ni affichées ni modifiées : {otherKeys.join(', ')}.
              </p>
            )}

            {warnCount > 0 && (
              <div className={styles.warnBox}>
                <AlertTriangle size={14} />
                <span>
                  {warnCount} ligne(s) à vérifier : prix de vente de base au-dessus du maximum, item en
                  doublon, ou prix manquant sur un item achetable ou vendable. L'enregistrement reste
                  possible, rien n'est bloqué.
                </span>
              </div>
            )}

            <div className={styles.tableHeadShop}>
              <span>Item</span>
              <span>Catégorie</span>
              <span>Prix d'achat</span>
              <span>Achat</span>
              <span>Vente</span>
              <span>Vente base</span>
              <span>Vente max</span>
              <span />
            </div>

            {visible.length === 0 && <p className={styles.muted}>Aucun item pour ces filtres.</p>}

            {visible.map(({ row, index }) => {
              const warns = warningsOf(row, duplicates.has(String(row.item ?? '').trim()));
              return (
                <div key={index} className={`${styles.rowEdit} ${warns.length ? styles.rowWarn : ''}`}>
                  <div className={styles.cellsShop}>
                    <span className={styles.picker}>
                      <SearchPicker
                        groupId={palette[0]?.groupId ?? ''}
                        value={row.item ?? ''}
                        options={itemOptions}
                        showThumbnails
                        placeholder="minecraft:diamond"
                        onChange={(v) => mutate(index, (x) => ({ ...x, item: v }))}
                      />
                    </span>
                    <span>
                      <select className={styles.input} value={row.category ?? ''}
                        onChange={(e) => mutate(index, (x) => ({ ...x, category: e.target.value }))}>
                        {!row.category && <option value="">—</option>}
                        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </span>
                    <span>
                      <input className={styles.input} type="number" step="any" min={0}
                        value={numOrEmpty(row.buyPrice)}
                        onChange={(e) => setNumber(index, 'buyPrice', e.target.value)} />
                    </span>
                    <span className={styles.checkCell} title="Achetable par les joueurs">
                      <input type="checkbox" checked={!!row.canBuy}
                        onChange={(e) => mutate(index, (x) => ({ ...x, canBuy: e.target.checked }))} />
                    </span>
                    <span className={styles.checkCell} title="Revendable par les joueurs">
                      <input type="checkbox" checked={!!row.canSell}
                        onChange={(e) => mutate(index, (x) => ({ ...x, canSell: e.target.checked }))} />
                    </span>
                    <span>
                      <input className={styles.input} type="number" step="any" min={0}
                        value={numOrEmpty(row.baseSellPrice)}
                        onChange={(e) => setNumber(index, 'baseSellPrice', e.target.value)} />
                    </span>
                    <span>
                      <input className={styles.input} type="number" step="any" min={0}
                        value={numOrEmpty(row.maxSellPrice)}
                        onChange={(e) => setNumber(index, 'maxSellPrice', e.target.value)} />
                    </span>
                    <span className={styles.rowActions}>
                      <button className={styles.iconBtn} title="Monter" onClick={() => move(index, -1)}><ChevronUp size={13} /></button>
                      <button className={styles.iconBtn} title="Descendre" onClick={() => move(index, 1)}><ChevronDown size={13} /></button>
                      <button className={styles.iconBtn} title="Dupliquer" onClick={() => duplicate(index)}><Copy size={13} /></button>
                      <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Supprimer" onClick={() => remove(index)}><Trash2 size={13} /></button>
                    </span>
                  </div>
                  {warns.length > 0 && <p className={styles.muted}>{warns.join(' · ')}</p>}
                </div>
              );
            })}

            <div className={styles.footerActions}>
              <button className={styles.secondaryBtn} onClick={addRow}>
                <Plus size={14} /> Ajouter un item
              </button>
              <span className={styles.muted}>{dirty ? 'Modifications non enregistrées.' : 'Enregistré.'}</span>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
